import axios from 'axios'
import { getAuthorizedConfiguration } from '../helpers/RequestHelper'

export const IS_LOADING_CONTENT_ASSETS = 'IS_LOADING_CONTENT_ASSETS'
export const SET_CONTENT_ASSETS = 'SET_CONTENT_ASSETS'
export const SET_CONTENT_ASSETS_ERROR = 'SET_CONTENT_ASSETS_ERROR'
export const IS_PUBLISHING_ASSET = 'IS_PUBLISHING_ASSET'
export const SET_DIRECTORY_FILTER_STATE = 'SET_DIRECTORY_FILTER_STATE'

export const CONTENT_ASSETS_LIST_ENDPOINT = '/content-assets'
export const CONTENT_ASSETS_LIST_QUERY_ENDPOINT = '/content-assets/list'

export const DEFAULT_PAGE_SIZE = 12
export const DEFAULT_ORDER_BY = 'creationDate'
export const DEFAULT_ORDER_TYPE = 'desc'

export const isLoadingContentAssets = (status) => ({
	type: IS_LOADING_CONTENT_ASSETS,
	status
})

export const setContentAssets = (payload) => ({
	type: SET_CONTENT_ASSETS,
	payload
})

export const setContentAssetsError = (error) => ({
	type: SET_CONTENT_ASSETS_ERROR,
	error
})

export const isPublishingAsset = (status) => ({
	type: IS_PUBLISHING_ASSET,
	status
})

/**
 * Mirror the parsed URL state into the reducer (`selectedCategoryId`,
 * `selectedTagIds`, `gridMode`). The list fetch is driven separately by the
 * thunk so these can be applied synchronously without waiting for I/O —
 * keeps the sidebar/tag-bar highlight in sync with the URL bar even while
 * the list refetches.
 */
export const setDirectoryFilterState = (payload) => ({
	type: SET_DIRECTORY_FILTER_STATE,
	payload
})

/**
 * Fetches a page of content assets from `POST /content-assets/list`.
 * The backend wraps the response in `{ total, pageNumber, pageSize, query,
 * orderBy, orderType, contentAssets: [...], categoryCounts: [...], allCount }`
 * — see backend slices `content-asset-read-path` + `content-asset-list-filters`.
 *
 * `categoryId` is single-select (null = All); the backend expands a parent id
 * server-side to include its children. `tagIds` is a multi-select OR filter
 * (empty/null = no tag filter).
 *
 * On failure the error is surfaced to the reducer so the page can render
 * its retry panel; this replaces the slice-1 "swallow-everything" behavior
 * because the page now has a first-class error state.
 */
export const loadContentAssets = (
	query = '',
	pageNumber = 1,
	pageSize = DEFAULT_PAGE_SIZE,
	categoryId = null,
	tagIds = []
) => {
	return async function(dispatch, getState) {
		dispatch(isLoadingContentAssets(true))
		try {
			const oidcUser = getState().oidc.user
			const { serviceManagementBackendUrl } = window.config
			const config = getAuthorizedConfiguration(oidcUser)
			const url = `${serviceManagementBackendUrl}${CONTENT_ASSETS_LIST_QUERY_ENDPOINT}`
			const body = {
				name: query,
				pageNumber,
				pageSize,
				orderBy: DEFAULT_ORDER_BY,
				orderType: DEFAULT_ORDER_TYPE,
				categoryId,
				tagIds: Array.isArray(tagIds) ? tagIds : []
			}
			const response = await axios.post(url, body, config)
			dispatch(setContentAssets(normalizeListResponse(response.data, query, pageNumber, pageSize)))
		} catch (error) {
			dispatch(setContentAssetsError(error))
		} finally {
			dispatch(isLoadingContentAssets(false))
		}
	}
}

/**
 * Re-shapes the list-endpoint response into the flat reducer state the FE
 * expects. Defends against unexpected shapes — if the server ever returns
 * something we don't understand, we default to an empty list rather than
 * crashing the page. The new `CategoryCounts` / `AllCount` fields default to
 * `[]` / `0` so an older BE (pre-`content-asset-list-filters`) still works.
 */
const normalizeListResponse = (data, query, pageNumber, pageSize) => {
	if (!data || typeof data !== 'object') {
		return {
			contentAssets: [],
			total: 0,
			pageNumber,
			pageSize,
			query,
			categoryCounts: [],
			allCount: 0
		}
	}
	return {
		contentAssets: Array.isArray(data.ContentAssets)
			? data.ContentAssets
			: Array.isArray(data.contentAssets)
			? data.contentAssets
			: [],
		total: typeof data.Total === 'number' ? data.Total : typeof data.total === 'number' ? data.total : 0,
		pageNumber: data.PageNumber || data.pageNumber || pageNumber,
		pageSize: data.PageSize || data.pageSize || pageSize,
		query: typeof data.Query === 'string' ? data.Query : typeof data.query === 'string' ? data.query : query,
		categoryCounts: Array.isArray(data.CategoryCounts)
			? data.CategoryCounts
			: Array.isArray(data.categoryCounts)
			? data.categoryCounts
			: [],
		allCount:
			typeof data.AllCount === 'number'
				? data.AllCount
				: typeof data.allCount === 'number'
				? data.allCount
				: 0
	}
}

/**
 * Submits the wizard's payload as multipart/form-data to POST /content-assets.
 *
 * Expected `payload`:
 *   - type:           'document' | 'image' | 'video' | 'link'   (string label)
 *   - title:          string
 *   - categoryId:     int
 *   - tagIds:         int[]
 *   - audienceCodes:  string[]   (when all 5 role codes are checked the wizard
 *                                 sends ['ALL']; backend normalizes either form)
 *   - file:           File | null  (for non-link types)
 *   - externalUrl:    string | null  (for link type)
 *
 * Returns a promise that resolves with the persisted asset DTO on 201, or
 * rejects with `{ status, body }` on any non-2xx so the caller can route the
 * UX (inline field errors, error toast, retry).
 */
export const createContentAsset = (payload) => {
	return async function(dispatch, getState) {
		dispatch(isPublishingAsset(true))
		try {
			const oidcUser = getState().oidc.user
			const { serviceManagementBackendUrl } = window.config
			const url = `${serviceManagementBackendUrl}${CONTENT_ASSETS_LIST_ENDPOINT}`

			// The backend expects an enum integer for `type` in the JSON command.
			const typeEnumValue = mapTypeLabelToEnum(payload.type)
			const command = {
				type: typeEnumValue,
				title: payload.title,
				categoryId: payload.categoryId,
				tagIds: payload.tagIds || [],
				audienceCodes: payload.audienceCodes || [],
				externalUrl: payload.type === 'link' ? payload.externalUrl : null
			}

			const formData = new FormData()
			formData.append('command', JSON.stringify(command))
			if (payload.type !== 'link' && payload.file) {
				formData.append('file', payload.file, payload.file.name)
			}

			// getAuthorizedConfiguration sets Content-Type: application/json which axios
			// would override for FormData, but to be safe we drop it explicitly so axios
			// can set the multipart boundary.
			const baseConfig = getAuthorizedConfiguration(oidcUser) || { headers: {} }
			const config = {
				...baseConfig,
				headers: {
					...(baseConfig.headers || {}),
					'Content-Type': 'multipart/form-data',
					Accept: 'application/json'
				}
			}

			const response = await axios.post(url, formData, config)
			return response.data
		} catch (error) {
			const status = error.response ? error.response.status : 0
			const body = error.response ? error.response.data : null
			const wrapped = new Error('createContentAsset failed')
			wrapped.status = status
			wrapped.body = body
			wrapped.original = error
			throw wrapped
		} finally {
			dispatch(isPublishingAsset(false))
		}
	}
}

const TYPE_ENUM = { document: 1, image: 2, video: 3, link: 4 }
const mapTypeLabelToEnum = (label) => TYPE_ENUM[label] || 0

/**
 * Update an existing ContentAsset via `PUT /content-assets/{id}` (slice 7).
 *
 * `payload` accepts the same shape as `createContentAsset` PLUS an `id` field
 * naming the row to update. The file is OPTIONAL — when omitted, the BE
 * preserves the existing FilePath. Tags + audiences are replacement, not
 * delta. Type is rejected with 400 if it differs from the existing row.
 *
 * Resolves with the updated DTO on 200; throws an enriched `{ status, body }`
 * error on any non-2xx so the caller can switch UX (toast vs inline error).
 */
export const updateContentAsset = (payload) => {
	return async function(dispatch, getState) {
		dispatch(isPublishingAsset(true))
		try {
			const oidcUser = getState().oidc.user
			const { serviceManagementBackendUrl } = window.config
			const url = `${serviceManagementBackendUrl}${CONTENT_ASSETS_LIST_ENDPOINT}/${encodeURIComponent(payload.id)}`

			const typeEnumValue = mapTypeLabelToEnum(payload.type)
			const command = {
				type: typeEnumValue,
				title: payload.title,
				categoryId: payload.categoryId,
				tagIds: payload.tagIds || [],
				audienceCodes: payload.audienceCodes || [],
				externalUrl: payload.type === 'link' ? payload.externalUrl : null
			}

			const formData = new FormData()
			formData.append('command', JSON.stringify(command))
			if (payload.type !== 'link' && payload.file) {
				formData.append('file', payload.file, payload.file.name)
			}

			const baseConfig = getAuthorizedConfiguration(oidcUser) || { headers: {} }
			const config = {
				...baseConfig,
				headers: {
					...(baseConfig.headers || {}),
					'Content-Type': 'multipart/form-data',
					Accept: 'application/json'
				}
			}

			const response = await axios.put(url, formData, config)
			return response.data
		} catch (error) {
			const status = error.response ? error.response.status : 0
			const body = error.response ? error.response.data : null
			const wrapped = new Error('updateContentAsset failed')
			wrapped.status = status
			wrapped.body = body
			wrapped.original = error
			throw wrapped
		} finally {
			dispatch(isPublishingAsset(false))
		}
	}
}

/**
 * Soft-delete a ContentAsset via `DELETE /content-assets/{id}` (slice 7).
 * Resolves on 200 with no value; throws the same enriched error shape on any
 * non-2xx.
 */
export const deleteContentAsset = (id) => {
	return async function(dispatch, getState) {
		try {
			const oidcUser = getState().oidc.user
			const { serviceManagementBackendUrl } = window.config
			const config = getAuthorizedConfiguration(oidcUser)
			const url = `${serviceManagementBackendUrl}${CONTENT_ASSETS_LIST_ENDPOINT}/${encodeURIComponent(id)}`
			await axios.delete(url, config)
		} catch (error) {
			const status = error.response ? error.response.status : 0
			const body = error.response ? error.response.data : null
			const wrapped = new Error('deleteContentAsset failed')
			wrapped.status = status
			wrapped.body = body
			wrapped.original = error
			throw wrapped
		}
	}
}
